// Use window.location.origin to always use the current domain
const API_URL = window.location.origin.includes('localhost')
  ? 'http://localhost:5003/api'
  : '/api';

export interface TableSchema {
  COLUMN_NAME: string;
  DATA_TYPE: string;
  IS_NULLABLE: string;
  CHARACTER_MAXIMUM_LENGTH: number | null;
  NUMERIC_PRECISION: number | null;
  NUMERIC_SCALE: number | null;
  COLUMN_DEFAULT: string | null;
  IS_PRIMARY_KEY: string;
  IS_IDENTITY: number;
}

export interface ApiResponse<T = any> {
  success?: boolean;
  error?: string;
  data?: T;
  rowsAffected?: number[];
}

class ApiService {
  private getRequestOptions(options: RequestInit = {}): RequestInit {
    return {
      ...options,
      credentials: 'include', // Include cookies for authentication
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };
  }

  async getTableData(schema: string, table: string, limit: number = 50, offset: number = 0): Promise<any[]> {
    const response = await fetch(`${API_URL}/table/${schema}/${table}?limit=${limit}&offset=${offset}`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to fetch table data');
    return response.json();
  }

  async getTableSchema(schema: string, table: string): Promise<TableSchema[]> {
    const response = await fetch(`${API_URL}/table/${schema}/${table}/schema`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to fetch schema');
    return response.json();
  }

  async getTableCount(schema: string, table: string): Promise<{ count: number }> {
    const response = await fetch(`${API_URL}/table/${schema}/${table}/count`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to fetch count');
    return response.json();
  }

  async createRecord(schema: string, table: string, data: any): Promise<ApiResponse> {
    const response = await fetch(`${API_URL}/table/${schema}/${table}`, this.getRequestOptions({
      method: 'POST',
      body: JSON.stringify(data)
    }));
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create record');
    }
    return response.json();
  }

  async updateRecord(schema: string, table: string, primaryKey: any, data: any): Promise<ApiResponse> {
    const response = await fetch(`${API_URL}/table/${schema}/${table}`, this.getRequestOptions({
      method: 'PUT',
      body: JSON.stringify({ primaryKey, data })
    }));
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update record');
    }
    return response.json();
  }

  async deleteRecord(schema: string, table: string, primaryKey: any): Promise<ApiResponse> {
    const response = await fetch(`${API_URL}/table/${schema}/${table}`, this.getRequestOptions({
      method: 'DELETE',
      body: JSON.stringify(primaryKey)
    }));
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete record');
    }
    return response.json();
  }

  async executeQuery(query: string) {
    const response = await fetch(`${API_URL}/query`, this.getRequestOptions({
      method: 'POST',
      body: JSON.stringify({ query })
    }));
    if (!response.ok) throw new Error('Query execution failed');
    return response.json();
  }

  async getSurveys() {
    const response = await fetch(`${API_URL}/surveys`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to fetch surveys');
    return response.json();
  }

  async getVisitTypes() {
    const response = await fetch(`${API_URL}/visittypes`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to fetch visit types');
    return response.json();
  }

  async getCurrentEnvironment(): Promise<{ environment: string }> {
    const response = await fetch(`${API_URL}/environment`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to get environment');
    return response.json();
  }

  async switchEnvironment(environment: string): Promise<{ success: boolean; environment: string; message: string }> {
    const response = await fetch(`${API_URL}/environment`, this.getRequestOptions({
      method: 'POST',
      body: JSON.stringify({ environment })
    }));
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to switch environment');
    }
    return response.json();
  }

  async compareTable(schema: string, table: string, sourceEnv?: 'DEV' | 'TEST' | 'PROD', targetEnv?: 'DEV' | 'TEST' | 'PROD') {
    const queryParams = new URLSearchParams();
    if (sourceEnv) queryParams.append('sourceEnv', sourceEnv);
    if (targetEnv) queryParams.append('targetEnv', targetEnv);

    const url = `${API_URL}/compare/${schema}/${table}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, this.getRequestOptions());
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to compare table');
    }
    return response.json();
  }

  async syncRecord(schema: string, table: string, record: any, sourceEnv: 'DEV' | 'TEST' | 'PROD', targetEnv: 'DEV' | 'TEST' | 'PROD') {
    const response = await fetch(`${API_URL}/sync/${schema}/${table}/record`, this.getRequestOptions({
      method: 'POST',
      body: JSON.stringify({ record, sourceEnv, targetEnv })
    }));
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sync record');
    }
    return response.json();
  }

  async syncAllRecords(schema: string, table: string, records: any[], sourceEnv: 'DEV' | 'TEST' | 'PROD', targetEnv: 'DEV' | 'TEST' | 'PROD', direction: 'only-in-source' | 'different-values' | 'all') {
    const response = await fetch(`${API_URL}/sync/${schema}/${table}/bulk`, this.getRequestOptions({
      method: 'POST',
      body: JSON.stringify({ records, sourceEnv, targetEnv, direction })
    }));
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sync records');
    }
    return response.json();
  }

  async deleteRecordByKey(schema: string, table: string, keyValue: any, targetEnv: 'DEV' | 'TEST' | 'PROD') {
    const response = await fetch(`${API_URL}/sync/${schema}/${table}/record`, this.getRequestOptions({
      method: 'DELETE',
      body: JSON.stringify({ keyValue, targetEnv })
    }));
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete record');
    }
    return response.json();
  }

  async getDomains() {
    const response = await fetch(`${API_URL}/domains`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to fetch domains');
    return response.json();
  }

  async getDomain(domainId: string) {
    const response = await fetch(`${API_URL}/domains/${domainId}`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to fetch domain');
    return response.json();
  }

  async getDomainTables(domainId: string) {
    const response = await fetch(`${API_URL}/domains/${domainId}/tables`, this.getRequestOptions());
    if (!response.ok) throw new Error('Failed to fetch domain tables');
    return response.json();
  }

  async copyTableFromEnvironment(schema: string, table: string, sourceEnv: string, targetEnv: string) {
    const response = await fetch(`${API_URL}/table/${schema}/${table}/copy`, this.getRequestOptions({
      method: 'POST',
      body: JSON.stringify({ sourceEnv, targetEnv })
    }));
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to copy table');
    }
    return response.json();
  }
}

export default new ApiService();